import { CheckOutlined } from '@mui/icons-material'
import {
  CircularProgress,
  IconButton,
  TextField,
  TextFieldProps,
  Tooltip,
} from '@mui/material'
import { t } from 'i18next'
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { makeStyles } from 'tss-react/mui'
import { Key } from 'utils/keyCodes'

type Props = {
  loading: boolean
  saveOnEnterFunction?: (value: string) => void
  onBlurFunction?: (value: string) => void
  loadingVariant?: 'helperText' | 'endAdornment'
  /**
   * if true the text field will not register key down events
   * util if changing another field that the current text field depends on
   */
  disableChanges?: boolean
} & TextFieldProps

const useStyles = makeStyles({ name: '' })((theme) => ({
  savedIcon: {
    opacity: 1,
    transition: 'opacity .5s ease',
  },
  hiddenSavedIcon: {
    opacity: '0 !important',
  },
}))

export const TextFieldWithLoading = (props: Props) => {
  const { classes } = useStyles()
  const {
    loading,
    saveOnEnterFunction,
    onBlurFunction,
    disableChanges = false,
    loadingVariant = 'helperText',
    onChange,
    ...textFieldProps
  } = props

  const handleSaveOnEnter = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>,
  ) => {
    if (disableChanges) {
      e.preventDefault()
      return false
    }
    if (!saveOnEnterFunction) {
      return true
    }

    if (
      (!props?.multiline && e.key === Key.Enter) ||
      (props?.multiline && e.key === Key.Enter && (e.ctrlKey || e.metaKey))
    ) {
      props.saveOnEnterFunction(
        e.currentTarget?.['value'] || e.target?.['value'],
      )
    }

    return true
  }

  const mounted = useRef(false)

  const [LoadingOrSaveIcon, setLoadingOrSaveIcon] =
    useState<React.ReactNode>(null)

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (
        e.currentTarget.value !==
        (textFieldProps.value ?? textFieldProps.defaultValue)
      ) {
        if (onChange) {
          onChange(e)
        }

        /* if (!onBlurFunction) {
          setLoadingOrSaveIcon(
            <div style={{ width: '24px' }}>
              <Tooltip
                title={t(
                  'common:unsavedChanges',
                  'unsaved changes. Press enter or click the save icon to save',
                )}
              >
                <IconButton
                  size="small"
                  color="info"
                  type="submit"
                  onClick={(e) => {
                    if (saveOnEnterFunction) {
                      e.preventDefault()
                      saveOnEnterFunction(e.currentTarget.value)
                    }
                  }}
                >
                  <SaveOutlined />
                </IconButton>
              </Tooltip>
            </div>,
          )
        } */
      }
    },
    [textFieldProps.value, textFieldProps.defaultValue, onChange],
  )

  useEffect(() => {
    if (loading === undefined || !mounted.current) {
      return
    }

    let loadingOrSaveIcon: React.ReactNode = null

    if (loading === false) {
      const iconId = crypto.randomUUID()
      loadingOrSaveIcon = (
        <Tooltip title={t('common:saved', 'saved')}>
          <IconButton size="small">
            <CheckOutlined
              color="success"
              className={classes.savedIcon}
              id={iconId}
              fontSize="small"
            />
          </IconButton>
        </Tooltip>
      )
      setTimeout(() => {
        if (mounted.current) {
          document
            .getElementById(iconId)
            ?.classList.add(classes.hiddenSavedIcon)
          setTimeout(() => {
            setLoadingOrSaveIcon(null)
          }, 500)
        }
      }, 500)
    } else if (loading === true) {
      loadingOrSaveIcon = (
        <Tooltip title={t('common:saving', 'saving')}>
          <CircularProgress
            size={'12px'}
            style={{ width: '12px', height: '12px' }}
          />
        </Tooltip>
      )
    }

    setLoadingOrSaveIcon(
      <div style={{ width: '24px' }}>{loadingOrSaveIcon}</div>,
    )
  }, [
    classes.hiddenSavedIcon,
    classes.savedIcon,
    loading,
    setLoadingOrSaveIcon,
  ])

  const lastKey = useRef<string | null>(null)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  return (
    <TextField
      {...textFieldProps}
      onChange={handleChange}
      onDoubleClick={(e) => {
        e.stopPropagation()
      }}
      onKeyDown={(e) => {
        if (e.key === Key.Escape) {
          lastKey.current = Key.Escape
        }

        if (props.onKeyDown) {
          props.onKeyDown(e)
          return
        } else if (handleSaveOnEnter) {
          handleSaveOnEnter(e)
          return
        }
      }}
      helperText={
        loadingVariant === 'helperText' && !props.helperText ? (
          <span
            style={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span style={{ flex: '1 1 100%' }}>
              {textFieldProps.helperText}
            </span>
            {LoadingOrSaveIcon}
          </span>
        ) : (
          props.helperText
        )
      }
      onBlur={(e) => {
        return props.onBlur
          ? props.onBlur(e)
          : props.onBlurFunction?.(e.currentTarget.value)
      }}
      onFocus={(e) => e.currentTarget.select()}
      onDrop={(e) => {
        e.preventDefault()
        const droppedValue = e.dataTransfer.getData('text/plain')
        if (droppedValue) {
          props.onChange?.({
            ...e,
            currentTarget: { ...e.currentTarget, value: droppedValue },
          } as any)

          if (saveOnEnterFunction) {
            saveOnEnterFunction(droppedValue)
          } else if (props.onKeyDown) {
            props.onKeyDown({
              ...e,
              currentTarget: { ...e.currentTarget, value: droppedValue },
              key: Key.Enter,
            } as any)
          } else if (props.onBlurFunction) {
            props.onBlurFunction(droppedValue)
          }
        }
      }}
      slotProps={{
        input: {
          ...(textFieldProps.slotProps?.input || {}),
          endAdornment:
            loadingVariant === 'endAdornment' &&
            (LoadingOrSaveIcon ??
              textFieldProps.slotProps?.input?.['endAdornment']),
        },
        htmlInput: {
          ...(textFieldProps.slotProps?.htmlInput || {}),
          style: {
            ...(textFieldProps.slotProps?.htmlInput?.['style'] || {}),
            paddingRight: '12px',
          },
        },
      }}
    />
  )
}
